"use client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Stack,
  useMediaQuery,
  Grid2,
} from "@mui/material";
import {
  SearchTwoTone as Search,
  FavoriteTwoTone as Favorite,
  DiamondTwoTone as Diamond,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useToast } from "../contexts/ToastContext";
import ProfileGrid from "../components/profile/ProfileGrid";
import CustomPagination from "../components/ui/CustomPagination";
import apiEndpoints from "../apiConfig";

const PROFILES_PER_PAGE = 12;

function HomePage() {
  const { user: authUser, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();

  // Enhanced media queries for different screen sizes
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const [profiles, setProfiles] = useState([]);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [enquiryStatuses, setEnquiryStatuses] = useState({}); // { receiver_id: status }

  // Convert isVerified to number
  const user = authUser
    ? { ...authUser, isVerified: Number(authUser.isVerified) }
    : null;

  useEffect(() => {
    if (!authUser) return;

    console.log("Fetching profiles for user.id:", authUser.id);

    fetch(
      `${apiEndpoints.getProfile}?page=${currentPage}&limit=${PROFILES_PER_PAGE}&user_id=${user.id}`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          // Remove logged-in user from results
          const filtered = data.profiles.filter(
            (p) => Number(p.id) !== Number(user.id),
          );

          setProfiles(filtered);
          setTotalProfiles(data.total - 1); // adjust total count
        } else {
          console.error("API Error:", data.error || "Unknown error");
        }
      })
      .catch((err) => console.error("Error fetching profiles:", err));

    // Fetch Enquiry Statuses
    fetch(`${apiEndpoints.checkEnquiryStatus}?requester_id=${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const statusMap = {};
          data.forEach((item) => {
            statusMap[item.receiver_id] = item.status;
          });
          setEnquiryStatuses(statusMap);
        }
      })
      .catch((err) => console.error("Error fetching enquiry statuses:", err));
  }, [currentPage, authUser]);

  const totalPages = Math.ceil(totalProfiles / PROFILES_PER_PAGE);
  const startIndex = (currentPage - 1) * PROFILES_PER_PAGE;
  const endIndex = startIndex + PROFILES_PER_PAGE;

  const handleViewProfile = (profileId) => navigate(`/profile/${profileId}`);
  const handleAddToWishlist = (profileId) =>
    console.log("Wishlist action for:", profileId);
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { showToast } = useToast();

  const handleSendEnquiry = (receiverId) => {
    fetch(apiEndpoints.sendEnquiry, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requester_id: user.user_id,
        receiver_id: receiverId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showToast("Enquiry sent", "success");
          setEnquiryStatuses((prev) => ({
            ...prev,
            [receiverId]: "pending",
          }));
        } else if (data.status === "exists") {
          showToast("Enquiry already sent", "info");
          setEnquiryStatuses((prev) => ({
            ...prev,
            [receiverId]: "pending",
          }));
        } else if (data.status === "blocked" || data.status === "no_credit") {
          showToast("Upgrade your plan to send enquiries", "error");
        } else {
          showToast(data.message || "Failed to send enquiry", "error");
        }
      })
      .catch((err) => {
        console.error("Error sending enquiry:", err);
        showToast("Network error", "error");
      });
  };

  // Merge profiles with their enquiry status
  const profilesWithStatus = profiles.map((p) => ({
    ...p,
    enquiryStatus: enquiryStatuses[p.id] || null,
    onSendEnquiry: handleSendEnquiry,
  }));

  if (!user) {
    return (
      <Container maxWidth="sm">{/* ... login prompt content ... */}</Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 3, md: 4 }, // Responsive padding
        position: "relative",
        px: { xs: 2, sm: 3, md: 4 }, // Horizontal padding for mobile
      }}
    >
      {/* Header Section - Improved responsive layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on larger screens
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "flex-end" },
          mb: { xs: 3, sm: 4 },
          gap: { xs: 2, sm: 0 }, // Gap for mobile layout
        }}
      >
        <Box
          sx={{
            order: { xs: 2, sm: 1 }, // Reorder for mobile
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: {
                xs: "1.5rem", // mobile
                sm: "2rem", // tablet
                md: "2.5rem", // desktop
              },
            }}
          >
            Discover Profiles
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
            }}
          >
            Showing {startIndex + 1}-{Math.min(endIndex, totalProfiles)} of{" "}
            {totalProfiles} profiles
          </Typography>
        </Box>

        {/* Advanced Search Button */}
        <Button
          variant="outlined"
          startIcon={!isMobile && <Search />} // Hide icon on very small screens if needed
          onClick={() => navigate("/search")}
          size={isMobile ? "small" : "medium"}
          color="secondary"
          sx={{
            order: { xs: 1, sm: 2 }, // Button first on mobile
            width: { xs: "100%", sm: "auto" }, // Full width on mobile
            borderWidth: 1.5,
            fontSize: {
              xs: "0.75rem",
              sm: "0.875rem",
            },
            px: { xs: 2, sm: 2 },
            py: { xs: 1, sm: 1 },
            "&:hover": {
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            },
          }}
        >
          {isMobile ? "Search" : "Advanced Search"}
        </Button>
      </Box>

      {/* Profile Grid - Ensure it's responsive */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <ProfileGrid
          profiles={profilesWithStatus}
          onViewProfile={handleViewProfile}
          onAddToWishlist={handleAddToWishlist}
          pageType="home"
        />
      </Box>

      {/* Pagination - Ensure it's mobile friendly */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: { xs: 2, sm: 3 },
        }}
      >
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Box>
    </Container>
  );
}

export default HomePage;
